import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export const HERO_SLIDES = ["video", "flow", "beforeAfter"] as const;
export type HeroSlide = (typeof HERO_SLIDES)[number];

/**
 * How long one tick lasts. The flow slide ticks once per job step, so it stays
 * on screen for `steps × flow` before the carousel moves on.
 */
export const HERO_TICK_MS: Record<HeroSlide, number> = {
  video: 10_000,
  flow: 2_800,
  beforeAfter: 9_000,
};

/** Phones never get the video slide: no video plays there, so a poster would only take space. */
export const HERO_PHONE_QUERY = "(max-width: 639px)";
const VIDEO_INDEX = HERO_SLIDES.indexOf("video");

interface Options {
  flowSteps: number;
  inView: boolean;
}

/**
 * Hero slideshow state. Time is driven by a CSS animation (see
 * HeroSlideControls) so pausing on hover resumes exactly where it stopped.
 * Choosing a slide or a step by hand stops autoplay; the toggle restarts it.
 */
export function useHeroCarousel({ flowSteps, inView }: Options) {
  const reducedMotion = usePrefersReducedMotion();
  const phone = useMediaQuery(HERO_PHONE_QUERY);
  const [index, setIndex] = useState(0);
  const [flowStep, setFlowStep] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (phone && index === VIDEO_INDEX) setIndex(VIDEO_INDEX + 1);
  }, [phone, index]);

  const slide = HERO_SLIDES[index];
  const canAutoplay = autoplay && !reducedMotion;

  const tick = useCallback(() => {
    if (slide === "flow" && flowStep < flowSteps - 1) {
      setFlowStep(flowStep + 1);
      return;
    }
    setFlowStep(0);
    const next = (index + 1) % HERO_SLIDES.length;
    setIndex(phone && next === VIDEO_INDEX ? next + 1 : next);
  }, [slide, flowStep, flowSteps, index, phone]);

  const show = useCallback((next: number) => {
    setIndex(next);
    setFlowStep(0);
    setAutoplay(false);
  }, []);

  const showStep = useCallback((step: number) => {
    setFlowStep(step);
    setAutoplay(false);
  }, []);

  const stop = useCallback(() => setAutoplay(false), []);
  const toggleAutoplay = useCallback(() => setAutoplay((value) => !value), []);

  return {
    slide,
    index,
    flowStep,
    reducedMotion,
    autoplay: canAutoplay,
    playing: canAutoplay && inView && !hovered && !focused,
    clockKey: `${index}-${slide === "flow" ? flowStep : 0}`,
    clockMs: HERO_TICK_MS[slide],
    tick,
    show,
    showStep,
    stop,
    toggleAutoplay,
    setHovered,
    setFocused,
  };
}
